import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogContentBase, DialogRef, DialogTitleBarComponent, DialogActionsComponent, KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { AppNotificationService } from '@app/core/services/custom-services/notification.service';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { UserService, OptinexUserDTO, AppLocale } from '@app/core/services/http-services/common/user.service';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { firstValueFrom } from 'rxjs';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { cancelIcon, saveIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { SelectLocaleComponent } from '../../select-locale/select-locale.component';
import { KENDO_FORM } from '@progress/kendo-angular-inputs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-user-settings-dialog',
  template: `
    <kendo-dialog-titlebar translate>App.UserSettings</kendo-dialog-titlebar>
    <ngx-spinner type="ball-scale-multiple"></ngx-spinner>
    <form
      kendoForm
      [formGroup]="form"
      (ngSubmit)="save()"
    >
      <kendo-formfield>
        <kendo-label
          [for]="languageDropdown"
          translate
          >App.Language</kendo-label
        >
        <app-select-locale
          #languageDropdown
          formControlName="localeId"
        ></app-select-locale>
      </kendo-formfield>
    </form>
    <kendo-dialog-actions>
      <button
        kendoButton
        (click)="cancel()"
        [svgIcon]="cancelIcon"
        title="{{ 'App.Cancel' | translate }}"
      ></button>
      <button
        kendoButton
        themeColor="primary"
        (click)="save()"
        [svgIcon]="saveIcon"
        title="{{ 'App.Save' | translate }}"
      ></button>
    </kendo-dialog-actions>
  `,
  styles: [
    `
      .dialog-content {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 16px 0;
        min-width: 300px;
      }
    `,
  ],
  imports: [TranslatePipe, TranslateDirective, FormsModule, ReactiveFormsModule, CommonModule, KENDO_FORM, KENDO_DIALOG, KENDO_BUTTON, KENDO_LABEL, NgxSpinnerModule, SelectLocaleComponent],
})
export class UserSettingsDialogComponent extends DialogContentBase {
  form: FormGroup<UpdateCurrentUserForm> = null;

  user: OptinexUserDTO;

  saveIcon: SVGIcon = saveIcon;
  cancelIcon: SVGIcon = cancelIcon;

  currentLocale: AppLocale;

  private readonly userService = inject(UserService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly spinner = inject(NgxSpinnerService);

  constructor(dialogRef: DialogRef) {
    super(dialogRef);
  }

  setupForm(user: OptinexUserDTO) {
    this.currentLocale = this.userService.getLocaleByCode(user.languageCode, user.localeCode);

    this.form = new FormGroup<UpdateCurrentUserForm>({
      localeId: new FormControl<number>(this.currentLocale?.id ?? 1),
      languageCode: new FormControl<string>(this.currentLocale?.language ?? ''),
      localeCode: new FormControl<string>(this.currentLocale?.locale ?? ''),
    });

    this.form.get('localeId').valueChanges.subscribe((localeId: number) => {
      const selectedLocale = this.userService.locales.find(locale => locale.id === localeId);
      if (selectedLocale) {
        this.form.patchValue({
          languageCode: selectedLocale.language,
          localeCode: selectedLocale.locale,
        });
      }
      this.currentLocale = selectedLocale;
    });
  }

  async save() {
    this.user.languageCode = this.form.value.languageCode;
    this.user.localeCode = this.form.value.localeCode;

    this.spinner.show();

    try {
      await firstValueFrom(this.userService.updateMe(this.user));
      this.dialog.close({ saved: true, locale: this.currentLocale });
    } catch {
      this.appNotificationService.notifyErrorAppChanel('App.ErrorSavingSettings', 'Error');
    } finally {
      this.spinner.hide();
    }
  }

  cancel() {
    this.dialog.close({ saved: false });
  }
}

export interface UpdateCurrentUserForm {
  localeId: FormControl<number>;
  languageCode: FormControl<string>;
  localeCode: FormControl<string>;
}
